/**
 * 地图水滴选点：鼠标变水滴跟随，点击取 WGS 坐标；不留下永久地图标记。
 * 复用会标水滴 SVG 视觉，与「发送位置」种会标区分（本工具不写图层）。
 */
import { fromMapLngLat } from './basemapCoord';

const CURSOR_CLASS = 'zhxt-chat-teardrop-cursor';
const STYLE_ID = 'zhxt-chat-teardrop-cursor-style';
const GHOST_ID = 'zhxt-chat-teardrop-ghost';

function teardropSvg(fill = '#e11d48') {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36">
  <path fill="${fill}" stroke="#fff" stroke-width="1.5"
    d="M14 1.5C8.2 1.5 3.5 6.4 3.5 12.5c0 7.4 8.2 18.2 9.7 20.1a1 1 0 0 0 1.6 0c1.5-1.9 9.7-12.7 9.7-20.1C24.5 6.4 19.8 1.5 14 1.5z"/>
  <circle cx="14" cy="12.5" r="4" fill="#fff"/>
</svg>`;
}

function ensureStyle() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
.${CURSOR_CLASS},
.${CURSOR_CLASS} .mapboxgl-canvas-container,
.${CURSOR_CLASS} .mapboxgl-canvas,
.${CURSOR_CLASS} canvas {
  cursor: none !important;
}
#${GHOST_ID} {
  position: fixed;
  width: 28px;
  height: 36px;
  margin-left: -14px;
  margin-top: -36px;
  pointer-events: none;
  z-index: 13000;
  background: url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(teardropSvg())}") no-repeat center / contain;
}
`;
  document.head.appendChild(style);
}

function ensureGhost() {
  if (typeof document === 'undefined') return null;
  let el = document.getElementById(GHOST_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = GHOST_ID;
    document.body.appendChild(el);
  }
  return el;
}

function removeGhost() {
  try { document.getElementById(GHOST_ID)?.remove?.(); } catch (_) { /* ignore */ }
}

/**
 * @param {object} opts
 * @param {() => any} opts.getMap
 * @param {() => string} [opts.getBasemapId]
 * @param {(result: { lat: number, lng: number, displayLng: number, displayLat: number }) => void} opts.onPick
 * @param {() => void} [opts.onCancel]
 * @returns {{ cancel: () => void }}
 */
export function startMapTeardropPick({ getMap, getBasemapId, onPick, onCancel }) {
  const map = getMap?.();
  if (!map) {
    const err = new Error('地图未就绪');
    err.code = 'map_not_ready';
    throw err;
  }
  ensureStyle();
  const container = map.getContainer?.();
  if (container) container.classList.add(CURSOR_CLASS);
  try { map.getCanvas().style.cursor = 'none'; } catch (_) { /* ignore */ }
  const ghost = ensureGhost();

  const moveGhost = (clientX, clientY) => {
    if (!ghost) return;
    ghost.style.left = `${clientX}px`;
    ghost.style.top = `${clientY}px`;
    ghost.style.display = 'block';
  };

  const onMouseMove = (e) => {
    const oe = e?.originalEvent;
    if (oe && Number.isFinite(oe.clientX)) moveGhost(oe.clientX, oe.clientY);
  };

  const onKeyDown = (ev) => {
    if (ev.key === 'Escape') {
      cleanup();
      onCancel?.();
    }
  };

  let finished = false;
  const cleanup = () => {
    if (finished) return;
    finished = true;
    try { map.off('mousemove', onMouseMove); } catch (_) { /* ignore */ }
    try { map.off('click', onClick); } catch (_) { /* ignore */ }
    try { window.removeEventListener('keydown', onKeyDown); } catch (_) { /* ignore */ }
    if (container) container.classList.remove(CURSOR_CLASS);
    try { map.getCanvas().style.cursor = ''; } catch (_) { /* ignore */ }
    removeGhost();
  };

  const onClick = (e) => {
    if (finished) return;
    const displayLng = Number(e?.lngLat?.lng);
    const displayLat = Number(e?.lngLat?.lat);
    if (!Number.isFinite(displayLng) || !Number.isFinite(displayLat)) return;
    const basemapId = getBasemapId?.() || null;
    const wgs = fromMapLngLat(displayLng, displayLat, basemapId, map) || [displayLng, displayLat];
    const lng = Number(wgs[0]);
    const lat = Number(wgs[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return;
    cleanup();
    onPick?.({ lat, lng, displayLng, displayLat });
  };

  map.on('mousemove', onMouseMove);
  map.on('click', onClick);
  window.addEventListener('keydown', onKeyDown);

  return { cancel: cleanup };
}
